import {
    FETCH_DAILY_STEPS_REPORTS,
    FETCH_DAILY_STEPS_REPORTS_FETCHING,
    FETCH_DAILY_STEPS_REPORTS_SUCCESS,
    FETCH_DAILY_STEPS_REPORTS_FAIL,
    FETCH_WEEKLY_STEPS_REPORTS,
    FETCH_WEEKLY_STEPS_REPORTS_FETCHING,
    FETCH_WEEKLY_STEPS_REPORTS_SUCCESS,
    FETCH_WEEKLY_STEPS_REPORTS_FAIL,
    FETCH_MONTHLY_STEPS_REPORTS,
    FETCH_MONTHLY_STEPS_REPORTS_FETCHING,
    FETCH_MONTHLY_STEPS_REPORTS_SUCCESS,
    FETCH_MONTHLY_STEPS_REPORTS_FAIL,
    SWITCH_STEPS_METRIC_PERIOD
} from '../../actions/stepsActions';
import { RESET_PROFILE_PAGE } from '../../actions/ActionTypes.js';

const INITIAL_STATE = {
    dailySteps: [],
    dailyStepsFetching: false,
    dailyStepsFetchFail: false,
    dailyStepsAverage: null,
    last7DaysAverage: null,
    weeklySteps: [],
    weeklyStepsFetching: false,
    weeklyStepsFetchFail: false,
    monthlySteps: [],
    monthlyStepsFetching: false,
    monthlyStepsFetchFail: false,
    metricPeriodFilter: 'weekly'
};

// ======= Daily Fetching Handlers =======
const fetchDailyStepsFetching = (state) => {
    return {
        ...state,
        dailyStepsFetching: true,
        dailyStepsFetchFail: false
    };
};

const fetchDailyStepsSuccess = (state, action) => {
    if (action.isLast7Days) {
        return {
            ...state,
            last7DaysAverage: action.dailySteps.aggregations,
            dailyStepsFetching: false,
            dailyStepsFetchFail: false
        };
    }
    return {
        ...state,
        dailySteps: action.dailySteps.values,
        dailyStepsAverage: action.dailySteps.aggregations,
        dailyStepsFetching: false,
        dailyStepsFetchFail: false
    };
};

const fetchDailyStepsFail = (state) => {
    return {
        ...state,
        dailyStepsFetching: false,
        dailyStepsFetchFail: true
    };
};

// ======= Weekly Fetching Handlers =======
const fetchWeeklyStepsFetching = (state) => {
    return {
        ...state,
        weeklyStepsFetching: true,
        weeklyStepsFetchFail: false
    };
};

const fetchWeeklyStepsSuccess = (state, action) => {
    return {
        ...state,
        weeklySteps: action.weeklySteps,
        weeklyStepsFetching: false,
        weeklyStepsFetchFail: false
    };
};

const fetchWeeklyStepsFail = (state) => {
    return {
        ...state,
        weeklyStepsFetching: false,
        weeklyStepsFetchFail: true
    };
};

// ======= Monthly Fetching Handlers =======
const fetchMonthlyStepsFetching = (state) => {
    return {
        ...state,
        monthlyStepsFetching: true,
        monthlyStepsFetchFail: false
    };
};

const fetchMonthlyStepsSuccess = (state, action) => {
    return {
        ...state,
        monthlySteps: action.monthlySteps,
        monthlyStepsFetching: false,
        monthlyStepsFetchFail: false
    };
};

const fetchMonthlyStepsFail = (state) => {
    return {
        ...state,
        monthlyStepsFetching: false,
        monthlyStepsFetchFail: true
    };
};

const switchFilter = (state, { payload: { filter } }) => ({
    ...state,
    metricPeriodFilter: filter
});

const resetToInitialState = () => INITIAL_STATE;

// ======= Handler Assignments =======
const handlers = {
    [FETCH_DAILY_STEPS_REPORTS_FETCHING]: fetchDailyStepsFetching,
    [FETCH_DAILY_STEPS_REPORTS_SUCCESS]: fetchDailyStepsSuccess,
    [FETCH_DAILY_STEPS_REPORTS_FAIL]: fetchDailyStepsFail,
    [FETCH_WEEKLY_STEPS_REPORTS_FETCHING]: fetchWeeklyStepsFetching,
    [FETCH_WEEKLY_STEPS_REPORTS_SUCCESS]: fetchWeeklyStepsSuccess,
    [FETCH_WEEKLY_STEPS_REPORTS_FAIL]: fetchWeeklyStepsFail,
    [FETCH_MONTHLY_STEPS_REPORTS_FETCHING]: fetchMonthlyStepsFetching,
    [FETCH_MONTHLY_STEPS_REPORTS_SUCCESS]: fetchMonthlyStepsSuccess,
    [FETCH_MONTHLY_STEPS_REPORTS_FAIL]: fetchMonthlyStepsFail,
    [RESET_PROFILE_PAGE]: resetToInitialState,
    [SWITCH_STEPS_METRIC_PERIOD]: switchFilter
};

// ======= Default Action ======
export default function stepsReports(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}

// ======= Actions =======
export function fetchDailySteps(payload, evictCache) {
    return {
        payload: payload,
        type: FETCH_DAILY_STEPS_REPORTS,
        meta: {
            cacheTime: evictCache ? null : 1000
        }
    };
}

export function fetchWeeklySteps(payload, evictCache) {
    return {
        type: FETCH_WEEKLY_STEPS_REPORTS,
        payload: payload,
        meta: {
            cacheTime: evictCache ? null : 1000
        }
    };
}

export function fetchMonthlySteps(payload, evictCache) {
    return {
        type: FETCH_MONTHLY_STEPS_REPORTS,
        payload: payload,
        meta: {
            cacheTime: evictCache ? null : 1000
        }
    };
}

export function switchMetricFilter(payload) {
    return {
        payload,
        type: SWITCH_STEPS_METRIC_PERIOD
    };
}

// ======= Selectors =======
export const getDailySteps = (state) => {
    if (state.stepsReports.dailySteps) {
        return state.stepsReports.dailySteps.map((item) => ({ calendarDate: item.calendarDate, ...item.values }));
    }
    return null;
};

export const getDailyStepsAverage = (state) => {
    return state.stepsReports.dailyStepsAverage;
};

export const getLast7DaysAverage = (state) => {
    return state.stepsReports.last7DaysAverage;
};

export const isDailyStepsFetching = (state) => {
    return state.stepsReports.dailyStepsFetching;
};

export const didDailyStepsFetchFail = (state) => {
    return state.stepsReports.dailyStepsFetchFail;
};

export const getWeeklySteps = (state) => {
    return state.stepsReports.weeklySteps ? state.stepsReports.weeklySteps : null;
};

export const isWeeklyStepsFetching = (state) => {
    return state.stepsReports.weeklyStepsFetching;
};

export const didWeeklyStepsFetchFail = (state) => {
    return state.stepsReports.weeklyStepsFetchFail;
};

export const getMonthlySteps = (state) => {
    return state.stepsReports.monthlySteps ? state.stepsReports.monthlySteps : null;
};

export const isMonthlyStepsFetching = (state) => {
    return state.stepsReports.monthlyStepsFetching;
};

export const didMonthlyStepsFetchFail = (state) => {
    return state.stepsReports.monthlyStepsFetchFail;
};
export const getMetricPeriodFilter = (state) => {
    return state.stepsReports.metricPeriodFilter;
};
